import type { ReadinessValue } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import dayjs from 'dayjs'
import { Link } from 'expo-router'
import { Pressable, View } from 'react-native'
import ReadinessBadge from '~/components/feedback/FeedbackMatchBadge'
import { DisplayValue } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Text } from '~/components/ui/text'
import type { FeedbackRequest } from '~/data/feedback/types'
import { cn } from '~/lib/utils'

export default function FeedbackListItem({
  request,
  className,
  showContactName = false,
  interactive = true,
  readiness,
}: {
  request: FeedbackRequest
  className?: string
  showContactName?: boolean
  interactive?: boolean
  readiness?: ReadinessValue | null
}) {
  const { t } = useTranslations('feedback')

  const getStatusInfo = () => {
    if (request.completed_at) {
      return {
        status: t(($) => $.answers.answered),
        timestamp: request.completed_at,
        icon: 'check-circle',
      }
    }

    const firstSentNotification = request.notifications
      ?.filter((notification) => notification.status === 'sent')
      .sort((notificationA, notificationB) =>
        notificationA.sent_at.localeCompare(notificationB.sent_at)
      )[0]

    if (firstSentNotification) {
      return {
        status:
          firstSentNotification.method === 'sms'
            ? t(($) => $.requestFlow.sentBySms)
            : t(($) => $.requestFlow.sentByEmail),
        timestamp: firstSentNotification.sent_at,
        icon: 'paper-plane',
      }
    }

    if (request.send_at) {
      return {
        status: t(($) => $.requestFlow.requestSent),
        timestamp: request.send_at,
        icon: 'clock',
      }
    }

    return {
      status: t(($) => $.requestFlow.cancel),
      timestamp: request.created_at,
      icon: 'pause-circle',
    }
  }

  const statusInfo = getStatusInfo()

  const content = (
    <>
      <View className="flex-1 gap-2 p-2">
        {showContactName && (
          <Heading2 className="font-light text-xl/none">{request.contact?.name}</Heading2>
        )}
        <DisplayValue className="text-sm">{t(($) => $.flow.title)}</DisplayValue>
        <View className="flex-row items-center gap-1">
          <Text className={cn('font-medium text-xs text-secondary')}>{statusInfo.status}</Text>
          <Text className="font-light text-sm text-secondary">
            {dayjs(statusInfo.timestamp).format('DD.MM.YYYY HH:mm')}
          </Text>
        </View>
      </View>

      <ReadinessBadge readiness={readiness ?? null} className="ml-auto self-center" />
    </>
  )

  const wrapperClassName = cn(
    'group/button native:shadow-card mx-2 my-1 flex-row justify-between gap-1 rounded-xl bg-background p-1.5 shadow-md shadow-primary/20',
    className
  )

  if (!interactive) {
    return <View className={wrapperClassName}>{content}</View>
  }

  return (
    <Link
      href={{
        pathname: '/leads/[leadId]',
        params: { leadId: request.contact_id },
      }}
      asChild>
      <Pressable className={wrapperClassName}>{content}</Pressable>
    </Link>
  )
}
